import {
  Button,
  FormControl,
  FormLabel,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
} from "@chakra-ui/react";
import { t } from "i18next";
import { useState } from "react";
import { FaCheck } from "react-icons/fa";
import { ContactPersonDto, EmailInviteLanguage, useUpdateOrganizationEntryMutation } from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { PhoneNumberInput } from "../../../../common/input/PhoneNumberInput";
import { Explanation } from "../../../../common/support/Explanation/Explanation";
import { useBaseData } from "../../../../common/useBaseData";

const initContactPerson = (): ContactPersonDto => ({
  name: "",
  email: "",
  language: EmailInviteLanguage.No,
  title: "",
});

interface Props {
  entries: { id: string; name: string }[];
  onClose: () => void;
}

export const OrgEntryContactPersonAddForm = ({ entries, onClose }: Props) => {
  const baseData = useBaseData();
  const displayer = useApiError();
  const [updateOrgEntry, { isLoading }] = useUpdateOrganizationEntryMutation();

  const [selectedEntryId, setSelectedEntryId] = useState(entries[0]?.id ?? "");
  const [contactPerson, setContactPerson] = useState<ContactPersonDto>(initContactPerson());

  const getPhoneNumberInput = (): PhoneNumberInput | null => {
    if (!contactPerson.phoneNumber) return null;
    const callingCode = baseData.callingCodes.find(
      (c) => c.countryAlpha3Code === contactPerson.phoneNumber?.callingCode.countryAlpha3Code
    );
    return {
      number: contactPerson.phoneNumber.number,
      country: {
        name: callingCode?.countryName ?? "",
        alpha3Code: callingCode?.countryAlpha3Code ?? "",
        callingCode: callingCode?.value ?? "",
      },
    };
  };

  const isValid = !!contactPerson.email && !!contactPerson.name && !!selectedEntryId;

  const handleSave = async () => {
    try {
      await updateOrgEntry({
        entryId: selectedEntryId,
        updateOrganizationEntryRequest: { addContactPerson: contactPerson },
      }).unwrap();
      onClose();
    } catch (e) {
      displayer.trigger(e);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("Add contact person")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {entries.length > 1 && (
            <FormControl pb="2">
              <FormLabel fontSize="sm">{t("Company")}</FormLabel>
              <Select
                size="sm"
                rounded="md"
                backgroundColor="smBackground"
                value={selectedEntryId}
                onChange={(e) => setSelectedEntryId(e.target.value)}
              >
                {entries.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name}
                  </option>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl>
            <FormLabel fontSize="sm">{t("Name")}</FormLabel>
            <Input
              autoComplete="off"
              size="sm"
              rounded="md"
              backgroundColor="smBackground"
              value={contactPerson.name}
              onChange={(e) => setContactPerson({ ...contactPerson, name: e.target.value })}
            />
          </FormControl>
          <FormControl pt="2">
            <FormLabel fontSize="sm">{t("Title")}</FormLabel>
            <Input
              autoComplete="off"
              size="sm"
              rounded="md"
              backgroundColor="smBackground"
              value={contactPerson.title ?? ""}
              onChange={(e) => setContactPerson({ ...contactPerson, title: e.target.value })}
            />
          </FormControl>
          <FormControl pt="2">
            <FormLabel fontSize="sm">{t("Email")}</FormLabel>
            <Input
              autoComplete="off"
              size="sm"
              rounded="md"
              backgroundColor="smBackground"
              value={contactPerson.email}
              onChange={(e) => setContactPerson({ ...contactPerson, email: e.target.value })}
            />
          </FormControl>
          <FormControl pt="2">
            <FormLabel fontSize="sm">{t("Phone number")}</FormLabel>
            <PhoneNumberInput
              size="sm"
              value={getPhoneNumberInput()}
              onChange={(phoneNumber) =>
                setContactPerson({
                  ...contactPerson,
                  phoneNumber: phoneNumber
                    ? {
                        callingCode: {
                          countryAlpha3Code: phoneNumber.country?.alpha3Code ?? "",
                          value: phoneNumber.country?.callingCode ?? "",
                          countryName: phoneNumber.country?.name ?? "",
                        },
                        number: phoneNumber.number,
                      }
                    : undefined,
                })
              }
            />
          </FormControl>
          <FormControl pt="2">
            <FormLabel fontSize="sm">{t("Invitation language")}</FormLabel>
            <Select
              value={contactPerson.language}
              size="sm"
              rounded="md"
              backgroundColor="smBackground"
              onChange={(e) => setContactPerson({ ...contactPerson, language: e.target.value as EmailInviteLanguage })}
            >
              <option value="No">{t("Norwegian")}</option>
              <option value="En">{t("English")}</option>
            </Select>
          </FormControl>
        </ModalBody>
        <ModalFooter columnGap={2}>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Explanation text={t("Make sure at least email and name is valid")} enabled={!isValid}>
            <Button
              colorScheme="teal"
              size="sm"
              variant="outline"
              rightIcon={<Icon as={FaCheck} />}
              isLoading={isLoading}
              isDisabled={!isValid}
              onClick={handleSave}
            >
              {t("Add contact person")}
            </Button>
          </Explanation>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
